import React, { useState, useEffect } from "react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isToday } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import logger from "../../utils/logger";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';

import { useAlert } from '../../providers/AlertProvider';
import { useAuthProvider } from '../../providers/AuthProvider';

import { ActivityIndicator } from '../custom/ActivityIndicator';
import AddHolidayModal from '../custom/AddHolidayModal';
import RequestHolidayModal from '../custom/RequestHolidayModal';
import { HomePageHolidays } from '../../functions/home_page/home_page_holidays';
import { getDateRangeForQuery } from '../lib/functions';

import "../css/Calendar.css";

const Holidays = ({ theme, drivers = [], admins = [], warehouseStaff = [], 
                   pendingHolidayRequests, holidayRequestsCount,
                   showHolidayRequestsModal, setShowHolidayRequestsModal, 
                   holidaysData = [], setHolidaysData }) => {
    const { showAlert } = useAlert();
    const { isAdmin, isManager, isDev, permissions } = useAuthProvider();
    const canEdit = isDev || permissions.perm_holidays_edit;

    const [isLoading, setIsLoading] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [openModal, setOpenModal] = useState(false);

    // Filter users to only include those with holidays allocation
    const filteredDrivers = drivers.filter(driver => driver.holidays !== null && driver.holidays !== undefined);
    const filteredAdmins = admins.filter(admin => admin.holidays !== null && admin.holidays !== undefined);
    const filteredWarehouse = warehouseStaff.filter(staff => staff.holidays !== null && staff.holidays !== undefined);

    // Initialize holidays functions
    const holidaysFunctions = new HomePageHolidays(showAlert);

    // Fetching holidays data
    const fetchData = async () => {
        if (!currentDate) return;
        
        const range = getDateRangeForQuery(currentDate);
        logger.log("Fetching holidays for range: ", range.from, range.to);
        await holidaysFunctions.fetchHolidays(setHolidaysData, setIsLoading, range);
    };

    // Trigger the fetchData function on component mount and when currentDate changes
    useEffect(() => {
        fetchData();
    }, [currentDate]);

    // Count annual leave days for a user in a specific year
    const countAnnualLeaveDays = (userId, year) => {
        return holidaysData.filter(holiday => {
            const holidayYear = new Date(holiday.date).getFullYear();
            return holiday.user_id === userId && 
                   holiday.holiday_type === 'annual_leave' && 
                   holidayYear === year;
        }).length;
    };

    // Get user's holiday allocation
    const getUserHolidayAllocation = (userId) => {
        const allUsers = [...filteredDrivers, ...filteredAdmins, ...filteredWarehouse];
        const user = allUsers.find(u => u.id === userId);
        return user ? user.holidays : 20; // fallback to 20 if not found
    };

    // Handle add holiday with annual leave validation
    const handleAddHoliday = async (holidayData) => {
        // Proceed directly to holiday addition (weekend restriction removed)
        await proceedWithHolidayAddition(holidayData);
    };

    // Separate function for holiday addition logic
    const proceedWithHolidayAddition = async (holidayData) => {
        // Check for annual leave limit if the holiday type is annual leave
        if (holidayData.holidayType === 'annual_leave') {
            const selectedYear = new Date(holidayData.startDate).getFullYear();
            const currentAnnualLeaveDays = countAnnualLeaveDays(holidayData.userId, selectedYear);
            const userAllocation = getUserHolidayAllocation(holidayData.userId);
            
            if (currentAnnualLeaveDays >= userAllocation) {
                showAlert({
                    message: `This user has already used all ${userAllocation} annual leave days for ${selectedYear}. Cannot add more annual leave.`,
                    type: 'warning',
                    buttons: [
                        {
                            label: 'OK',
                            type: 'primary',
                            onClick: () => {}
                        }
                    ]
                });
                return; // Don't proceed with adding the holiday
            }
        }

        await holidaysFunctions.addHoliday(holidayData, setHolidaysData, setIsLoading, fetchData);
        setOpenModal(false);
        setSelectedDate(null);
    };

    // Handle delete holiday
    const handleDeleteHoliday = async (holidayId) => {
        showAlert({
            message: 'Are you sure you want to delete this holiday?',
            type: 'warning',
            buttons: [
                {
                    label: 'Cancel',
                    type: 'secondary',
                    onClick: () => {}
                },
                {
                    label: 'Delete',
                    type: 'danger',
                    onClick: () => holidaysFunctions.deleteHoliday(holidayId, setHolidaysData, setIsLoading, fetchData)
                }
            ]
        });
    };

    // Check if the day has any holidays
    const hasHoliday = (dayEvents) => {
        return dayEvents.length > 0;
    };

    // Get holidays for a specific date
    const getHolidaysForDate = (dateString) => {
        return holidaysData.filter(holiday => holiday.date === dateString);
    };

    // Get user name from user_id
    const getUserName = (userId) => {
        const allUsers = [...filteredDrivers, ...filteredAdmins, ...filteredWarehouse];
        const user = allUsers.find(u => u.id === userId);
        return user ? `${user.name} ${user.surname}` : 'Unknown User';
    };

    // Get user data from user_id
    const getUserData = (userId) => {
        const allUsers = [...filteredDrivers, ...filteredAdmins, ...filteredWarehouse];
        return allUsers.find(u => u.id === userId);
    };

    // Get color based on user group and holiday type
    const getUserColor = (userId, holidayType) => {
        const user = getUserData(userId);
        
        // If it's not annual leave, always show red
        if (holidayType !== 'annual_leave') {
            return '#dc3545'; // Red for non-annual leave holidays
        }
        
        // For annual leave, use group-based colors
        if (!user) return '#007bff'; // Default blue
        
        if (user.group === 'DRIVER') {
            return '#007bff'; // Blue for drivers
        } else if (user.group === 'ADMIN') {
            return 'rgba(178, 4, 194, 0.7)'; // Purple for admins
        }
        return '#007bff'; // Default blue
    };

    // Generate the calendar
    const generateCalendar = () => {
        const startDate = startOfWeek(startOfMonth(currentDate));
        const endDate = endOfWeek(endOfMonth(currentDate));
        const days = [];
        let day = startDate;

        while (day <= endDate) {
            const formattedDate = format(day, "yyyy-MM-dd");
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const dayHolidays = getHolidaysForDate(formattedDate);
            
            days.push(
                <motion.div
                    key={formattedDate}
                    className={`day ${isCurrentMonth ? "current-month" : "other-month"} ${
                        isToday(day) ? "today" : ""
                    }`}
                    onClick={() => {
                        setSelectedDate(formattedDate);
                        if (isAdmin) {
                            setOpenModal(true);
                        }
                    }}
                    whileHover={{ scale: 1.05 }}
                    style={{
                        height: '12.5vh',
                        zIndex: 0,
                        background: hasHoliday(dayHolidays) 
                            ? 'rgba(0, 123, 255, 0.3)' // Light blue background for holidays
                            : null,
                        cursor: isAdmin ? 'pointer' : 'default',
                    }}
                >
                    <div className="date">{format(day, "d")}</div>
                    <div className="event-container"
                        style={{
                            height: '100%',
                            overflowY: 'auto',
                            padding: 5,
                            paddingBottom: 25,
                            borderRadius: 5,
                        }}
                    >
                    {dayHolidays.map((holiday, index) => (
                        <motion.div
                            key={index}
                            className="event"
                            whileHover={{ scale: 1.05 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDate(formattedDate);
                            }}
                            style={{
                                background: getUserColor(holiday.user_id, holiday.holiday_type),
                                color: 'white',
                                padding: '3px',
                                borderRadius: '3px',
                                fontSize: '12px',
                                marginTop: '5px',
                                textAlign: 'center',
                                cursor: 'pointer'
                            }}
                        >
                            <span style={{ fontSize: '14px' }}>
                                {(() => { const u = getUserName(holiday.user_id).split(' '); return u.length >= 2 ? `${u[0][0]}. ${u.slice(1).join(' ')}` : u[0]; })()}
                            </span>
                        </motion.div>
                    ))}
                    </div>
                </motion.div>
            );
            day = addDays(day, 1);
        }
        return days;
    };

    // Render the day view when a date is selected
    const renderDayView = () => {
        if (!selectedDate) return null;
    
        const dayHolidays = getHolidaysForDate(selectedDate);
    
        return (
            <motion.div
                className="service-modal"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                style={{ zIndex: 1 }}
            >
                <div className="modal-content">
                    <h2>{format(new Date(selectedDate), "MMMM dd, yyyy")}</h2>
    
                    {dayHolidays.length > 0 ? (
                        dayHolidays.map((holiday, index) => (
                            <div key={index}>
                                <span className="modal-data">
                                    User: {getUserName(holiday.user_id)}
                                </span>
                                <span className="modal-data">
                                    Type: {holiday.holiday_type.replace('_', ' ')}
                                </span>
                                <span className="modal-data">
                                    Date: {format(new Date(holiday.date), "MMM dd, yyyy")}
                                </span>
                                {holiday.notes && (
                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                        <span
                                            className="modal-data"
                                            style={{ fontSize: 18, marginTop: 5, fontStyle: 'italic' }}
                                        >
                                            Notes: {holiday.notes}
                                        </span>
                                    </div>
                                )}

                                <div
                                    className="modal-buttons"
                                    style={{
                                        borderBottom: theme === 'dark' ? '1px solid #ccc' : '1px solid #333',
                                    }}
                                >
                                    {canEdit && (
                                        <button
                                            className="btn btn-danger"
                                            style={{ margin: 5 }}
                                            onClick={() => handleDeleteHoliday(holiday.id)}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p>No holidays for this date.</p>
                    )}
    
                    {canEdit && (
                    <button
                        className="btn btn-primary"
                        style={{ marginBottom: 10 }}
                        onClick={() => {
                            setOpenModal(true);
                        }}
                    >
                        Add Holiday
                    </button>
                    )}
                    <button
                        className="btn btn-secondary"
                        onClick={() => setSelectedDate(null)}
                    >
                        Close
                    </button>
                </div>
            </motion.div>
        );
    };

    return (
        <div id={theme} className="calendar" style={{ backgroundColor: theme === 'dark' ? '#080808' : '#fff', position: 'relative' }}>
            {/* Holiday Requests Button - Outside Container Top Left */}
            {isAdmin && holidayRequestsCount > 0 && (
                <button
                    onClick={() => setShowHolidayRequestsModal(true)}
                    style={{
                        position: 'absolute',
                        top: '-50px',
                        left: '-50px',
                        background: '#ff6b35',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                    title={`${holidayRequestsCount} pending holiday requests`}
                >
                    {holidayRequestsCount}
                </button>
            )}
            
            {/* Show loading spinner */}
            {isLoading && (
                <div>
                    <ActivityIndicator size={50} />
                </div>
            )}

            {/* Default Calendar View */}
            {!isLoading && (
                <>
                    <div className="header">
                        <button onClick={() => setCurrentDate(subMonths(currentDate, 1))}>&lt;</button>
                        <h2>{format(currentDate, "MMMM yyyy")}</h2>
                        <button onClick={() => setCurrentDate(addMonths(currentDate, 1))}>&gt;</button>
                    </div>
                    <div className="day-names">
                        <span>Sun</span>
                        <span>Mon</span>
                        <span>Tue</span>
                        <span>Wed</span>
                        <span>Thu</span>
                        <span>Fri</span>
                        <span>Sat</span>
                    </div>
                    <AnimatePresence mode="wait" style={{ zIndex: 0, position: 'static' }}>
                        <motion.div
                            key={currentDate}
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.5 }}
                            className="days-grid"
                            style={{ zIndex: 0, position: 'static' }}
                        >
                            {generateCalendar()}
                        </motion.div>
                    </AnimatePresence>
                    <AnimatePresence>{selectedDate && renderDayView()}</AnimatePresence>
                </>
            )}

            {/* Holiday Modals */}
            {canEdit ? (
                <AddHolidayModal
                    theme={theme}
                    isOpen={openModal}
                    onClose={() => {
                        setOpenModal(false);
                        setSelectedDate(null);
                    }}
                    onSubmit={handleAddHoliday}
                    drivers={filteredDrivers}
                    admins={filteredAdmins}
                    warehouseStaff={filteredWarehouse}
                    selectedDate={selectedDate}
                />
            ) : (
                <RequestHolidayModal
                    theme={theme}
                    isOpen={openModal}
                    onClose={() => {
                        setOpenModal(false);
                        setSelectedDate(null);
                    }}
                    selectedDate={selectedDate}
                    onSuccess={fetchData}
                />
            )}
        </div>
    );
};

export default Holidays;